import { useEffect, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import EmptyDogState from "@/components/app/EmptyDogState";
import { MAX_EBOOK_BYTES } from "@/lib/constants";
import { GraduationCap, Trash2, BookOpen, Download, Star, ExternalLink } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AcademyPage() {
  const { activeDog } = useDogs();
  const [webinars, setWebinars] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [form, setForm] = useState({
    webinar_name: "",
    watched_date: format(new Date(), "yyyy-MM-dd"),
    notes: "",
  });
  const [ebookForm, setEbookForm] = useState({
    title: "", author: "", notes: "", file_data: "", file_name: "", mime_type: "",
  });
  const [saving, setSaving] = useState(false);
  const [savingEbook, setSavingEbook] = useState(false);

  const load = async () => {
    if (!activeDog) return;
    const [wRes, eRes] = await Promise.all([
      api.get("/webinars", { params: { dog_id: activeDog.id } }),
      api.get("/ebooks", { params: { dog_id: activeDog.id } }),
    ]);
    setWebinars(wRes.data.items || []);
    setEbooks(eRes.data.items || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeDog]);

  if (!activeDog) return <EmptyDogState>{null}</EmptyDogState>;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.webinar_name) return;
    setSaving(true);
    try {
      await api.post("/webinars", { dog_id: activeDog.id, ...form });
      toast.success("Webinar added");
      setForm({ webinar_name: "", watched_date: format(new Date(), "yyyy-MM-dd"), notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this webinar entry?")) return;
    await api.delete(`/webinars/${id}`);
    load();
  };

  const onEbookFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_EBOOK_BYTES) {
      toast.error("Please choose a file under 8 MB");
      return;
    }
    const data = await fileToDataUrl(file);
    setEbookForm((f) => ({
      ...f,
      file_data: data,
      file_name: file.name,
      mime_type: file.type,
      title: f.title || file.name.replace(/\.[^.]+$/, ""),
    }));
  };

  const submitEbook = async (e) => {
    e.preventDefault();
    if (!ebookForm.title || !ebookForm.file_data) {
      toast.error("Add a title and choose a file");
      return;
    }
    setSavingEbook(true);
    try {
      await api.post("/ebooks", { dog_id: activeDog.id, ...ebookForm });
      toast.success("Ebook uploaded");
      setEbookForm({ title: "", author: "", notes: "", file_data: "", file_name: "", mime_type: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSavingEbook(false);
    }
  };

  const downloadEbook = async (b) => {
    try {
      const { data } = await api.get(`/ebooks/${b.id}`);
      const a = document.createElement("a");
      a.href = data.file_data;
      a.download = data.file_name || "ebook";
      a.click();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    }
  };

  const removeEbook = async (id) => {    if (!window.confirm("Delete this ebook?")) return;
    await api.delete(`/ebooks/${id}`);
    load();
  };

  return (
    <div className="space-y-10" data-testid="academy-page">
      <SectionTitle overline="Education" title="Education" />

      {/* Favorites */}
      <div data-testid="education-favorites">
        <div className="flex items-center gap-2 mb-3">
          <Star size={16} className="text-primary" />
          <h3 className="font-display text-lg font-bold tracking-tight">Favorites</h3>
        </div>
        <Card className="border-border shadow-none rounded-lg">
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {[
                { name: "Leading Edge Dog Show Academy", url: "https://www.leadingedgedogshowacademy.com/" },
                { name: "Sit Stay Squat", url: "https://www.sitstaysquat.ca/" },
              ].map((f) => (
                <li key={f.url}>
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`favorite-${f.url}`}
                    className="flex items-center gap-3 px-5 py-4 hover:bg-muted transition-colors"
                  >
                    <span className="h-9 w-9 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                      <GraduationCap size={18} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-medium leading-snug">{f.name}</span>
                      <span className="block text-xs text-muted-foreground truncate">{f.url.replace(/^https?:\/\//, "")}</span>
                    </span>
                    <ExternalLink size={14} className="text-muted-foreground shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
              <GraduationCap size={20} />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold tracking-tight">Webinars watched</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Log every Leading Edge Dog Show Academy webinar you&apos;ve watched with this dog —
                track your training education and revisit dates as needed.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border shadow-none rounded-lg">
          <CardContent className="p-0">
            {webinars.length === 0 ? (
              <p className="text-sm text-muted-foreground p-10 text-center">
                No webinars logged yet — add your first one →
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-6 py-3">Date watched</th>
                    <th className="px-6 py-3">Webinar</th>
                    <th className="px-6 py-3 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {webinars.map((w) => (
                    <tr key={w.id} className="border-t border-border" data-testid={`webinar-row-${w.id}`}>
                      <td className="px-6 py-3 whitespace-nowrap">
                        {format(parseISO(w.watched_date), "MMM d, yyyy")}
                      </td>
                      <td className="px-6 py-3">
                        <p className="font-medium">{w.webinar_name}</p>
                        {w.notes ? <p className="text-xs text-foreground/80 mt-1">{w.notes}</p> : null}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() => remove(w.id)}
                          className="text-muted-foreground hover:text-destructive"
                          data-testid={`delete-webinar-${w.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card className="border-border shadow-none rounded-lg">
          <CardContent className="p-6">
            <p className="font-medium mb-4">Add webinar</p>
            <form onSubmit={submit} className="space-y-3" data-testid="add-webinar-form">
              <div>
                <Label htmlFor="w-name">Webinar name</Label>
                <Input id="w-name" data-testid="webinar-name-input" value={form.webinar_name}
                  onChange={(e) => setForm({ ...form, webinar_name: e.target.value })} required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="w-date">Date watched</Label>
                <Input id="w-date" data-testid="webinar-date-input" type="date" value={form.watched_date}
                  onChange={(e) => setForm({ ...form, watched_date: e.target.value })} required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="w-notes">Notes</Label>
                <Textarea id="w-notes" data-testid="webinar-notes-input" rows={3} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5"
                  placeholder="Key takeaways, instructor, things to practice…" />
              </div>
              <Button type="submit" disabled={saving}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="submit-webinar-button">
                {saving ? "Saving…" : "Add webinar"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* LEDSA Ebooks */}
      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
              <BookOpen size={20} />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold tracking-tight">LEDSA ebooks</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Upload Leading Edge Dog Show Academy ebooks (PDF or EPUB) so they&apos;re always at hand. Files up to 8 MB.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border shadow-none rounded-lg">
          <CardContent className="p-0">
            {ebooks.length === 0 ? (
              <p className="text-sm text-muted-foreground p-10 text-center">No ebooks uploaded yet — add your first one →</p>
            ) : (
              <ul className="divide-y divide-border">
                {ebooks.map((b) => (
                  <li key={b.id} className="p-5 flex items-start gap-4" data-testid={`ebook-row-${b.id}`}>
                    <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center text-muted-foreground shrink-0">
                      <BookOpen size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{b.title}</p>
                      {b.author ? <p className="text-xs text-muted-foreground mt-0.5">{b.author}</p> : null}
                      <p className="text-[11px] text-muted-foreground/80 mt-0.5">{b.file_name}</p>
                      {b.notes ? <p className="text-sm text-foreground/80 mt-2">{b.notes}</p> : null}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" onClick={() => downloadEbook(b)}
                        className="p-2 hover:bg-muted rounded-md" title="Download"
                        data-testid={`download-ebook-${b.id}`}>
                        <Download size={16} />
                      </button>
                      <button onClick={() => removeEbook(b.id)}
                        className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive"
                        data-testid={`delete-ebook-${b.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="border-border shadow-none rounded-lg">
          <CardContent className="p-6">
            <p className="font-medium mb-4">Upload ebook</p>
            <form onSubmit={submitEbook} className="space-y-3" data-testid="add-ebook-form">
              <div>
                <Label htmlFor="eb-file">File (PDF / EPUB, ≤ 8 MB)</Label>
                <Input id="eb-file" data-testid="ebook-file-input" type="file"
                  accept="application/pdf,application/epub+zip,.epub"
                  onChange={onEbookFile} className="mt-1.5" />
                {ebookForm.file_name ? (
                  <p className="text-xs text-muted-foreground mt-1.5 truncate">Selected: {ebookForm.file_name}</p>
                ) : null}
              </div>
              <div>
                <Label htmlFor="eb-title">Title</Label>
                <Input id="eb-title" data-testid="ebook-title-input" value={ebookForm.title}
                  onChange={(e) => setEbookForm({ ...ebookForm, title: e.target.value })} required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="eb-author">Author (optional)</Label>
                <Input id="eb-author" data-testid="ebook-author-input" value={ebookForm.author}
                  onChange={(e) => setEbookForm({ ...ebookForm, author: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="eb-notes">Notes</Label>
                <Textarea id="eb-notes" data-testid="ebook-notes-input" rows={3} value={ebookForm.notes}
                  onChange={(e) => setEbookForm({ ...ebookForm, notes: e.target.value })} className="mt-1.5"
                  placeholder="Chapters that mattered, what to revisit…" />
              </div>
              <Button type="submit" disabled={savingEbook || !ebookForm.file_data}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="submit-ebook-button">
                {savingEbook ? "Uploading…" : "Upload ebook"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
